import { useEffect, useRef } from "react";
import { IS_CAPACITOR } from "@/lib/platform-detect";
import { useAppUpdateStore } from "@/stores/app-update";

/**
 * Polling SPA-версии для zero-downtime deploy (blue/green swap).
 *
 * Поток:
 *  1. На mount читаем `<meta name="app-version">` — это sha бандла,
 *     зашитый build-time через `vite.plugins/version-inject.ts`.
 *  2. Каждые 90 секунд `fetch('/version.json', { cache: 'no-store' })`.
 *     Caddy SPA-контейнер отдаёт его без HTTP-кэша из dist/ — после
 *     blue/green swap клиенты увидят новую версию мгновенно.
 *  3. Если `version.json.version !== currentSha` — пушим в `useAppUpdateStore`.
 *     UI показывает `<UpdateBanner />`, на следующей route-навигации идёт
 *     soft-reload (или 5-секундный авто-reload при `forceReload: true`).
 *
 * Pause-логика:
 *  - При `document.hidden` останавливаем интервал (background tab не должен
 *    жечь сеть и батарею).
 *  - На `visibilitychange → visible` делаем immediate check + перезапуск
 *    интервала. Это покрывает кейс «открыл вкладку утром после ночного деплоя».
 *
 * Capacitor (APK):
 *  - НЕ запускается. Native обновления идут через Capgo OTA, не через
 *    SPA-polling. См. `lib/init-ota.ts` (когда будет добавлен) и Phase 5 rules.
 *
 * Dev-режим:
 *  - В dev `meta[app-version]` отсутствует (плагин `apply: 'build'`),
 *    или содержит `dev` → хук no-op'ает на старте.
 */

const POLL_INTERVAL_MS = 90_000;
const VERSION_URL = "/version.json";

interface VersionPayload {
	version?: unknown;
	sha?: unknown;
	forceReload?: unknown;
}

function readMetaVersion(): string | null {
	if (typeof document === "undefined") return null;
	const meta = document.querySelector<HTMLMetaElement>('meta[name="app-version"]');
	const value = meta?.content?.trim();
	return value && value.length > 0 ? value : null;
}

async function fetchRemoteVersion(): Promise<{
	sha: string;
	forceReload: boolean;
} | null> {
	try {
		const res = await fetch(VERSION_URL, { cache: "no-store" });
		if (!res.ok) return null;
		const json = (await res.json()) as VersionPayload;
		// Поддерживаем оба ключа: `version` (наш формат — см. version-inject.ts)
		// и `sha` (CharacterHub-совместимость).
		const sha =
			typeof json.version === "string"
				? json.version
				: typeof json.sha === "string"
					? json.sha
					: null;
		if (!sha) return null;
		const forceReload = json.forceReload === true;
		return { sha, forceReload };
	} catch {
		// Сетевая ошибка / битый JSON — игнорируем тихо. Следующий тик попробует снова.
		return null;
	}
}

export function useAppVersion(): void {
	const setUpdate = useAppUpdateStore((s) => s.setUpdate);
	const currentShaRef = useRef<string | null>(null);
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	useEffect(() => {
		// В Capacitor SPA-polling не нужен — Capgo OTA отвечает за обновления.
		if (IS_CAPACITOR) return;

		const meta = readMetaVersion();
		// В dev (без плагина или sha='dev') нет смысла polling'ить.
		if (!meta || meta === "dev") return;
		currentShaRef.current = meta;

		const check = async () => {
			const remote = await fetchRemoteVersion();
			if (!remote) return;
			if (remote.sha !== currentShaRef.current) {
				setUpdate({ sha: remote.sha, forceReload: remote.forceReload });
			}
		};

		const start = () => {
			if (intervalRef.current != null) return;
			intervalRef.current = setInterval(() => {
				void check();
			}, POLL_INTERVAL_MS);
		};

		const stop = () => {
			if (intervalRef.current != null) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};

		const handleVisibility = () => {
			if (document.hidden) {
				stop();
			} else {
				// Immediate check + перезапуск интервала.
				void check();
				start();
			}
		};

		// Первая проверка сразу — на случай если пользователь открыл вкладку
		// уже после деплоя (восстановление сессии).
		void check();
		if (!document.hidden) start();
		document.addEventListener("visibilitychange", handleVisibility);

		return () => {
			stop();
			document.removeEventListener("visibilitychange", handleVisibility);
		};
	}, [setUpdate]);
}
